import { ConnectedAccountStatus, DiscoveryLevel, FeatureType, GetOrganizationProductResponse, GetPointResponse, OrganizationPurchaseHistory, PaymentTargetType } from '../../../types';
import { LitElement, css, html } from 'lit';
import { customElement, query, state } from 'lit/decorators';
import { delay, isDefined } from '../../../helpers/utils';

import { CreateProductComponent } from '../../create-product-component';
import { StateController } from '../../../helpers/state/state-management/state-controller';
import accountInfo from '../../../helpers/state/account-info';
import { getAllPointsPaginated } from '../../../helpers/api/points-api';
import { getMainPage } from '../../../helpers/page-name';
import { getOrganizationProducts } from '../../../helpers/api/products-api';
import { getOrganizationPurchaseHistory } from '../../../helpers/api/purchases-api';
import { loadStripeStatus } from '../../../helpers/api/stripe-api';
import { openChatWindow } from '../../../helpers/intercom';
import organizationInfo from '../../../helpers/state/organization-info';
import pointCache from '../../../helpers/state/point-cache';
import topicData from '../../../helpers/state/topic-data';

@customElement('admin-paywall-addon')
export class AdminPaywallAddon extends LitElement {
    amplitude_callsite: string = 'Paywall Settings Drawer';

    @query('#createProductComponent')
    createProductComponent?: CreateProductComponent;

    @state()
    selected_tab: string = 'Paywalls';

    @state()
    paywallProducts: GetOrganizationProductResponse[] = [];

    @state()
    purchaseHistory: OrganizationPurchaseHistory | undefined;

    @state()
    stripeInfo: ConnectedAccountStatus | undefined;

    @state()
    isEditingProduct: boolean = false;

    @state()
    previewedPoints: GetPointResponse[] = [];

    @state()
    tabIndex: number = 0;

    options: string[] = ['Paywalls', 'Preview Points', 'Webhooks (Coming Soon)'];

    private readonly bindTopicState = new StateController(this, topicData);
    private readonly bindAccountState = new StateController(this, accountInfo);

    async firstUpdated() {
        await this.startup();
    }

    private async startup() {
        // Wait for topic data to be ready.
        while (!topicData.isReady || !pointCache.isReady) {
            await delay(500);
        }
        await this.getPaywallProducts();
        await this.loadPaymentData();
    }

    async getPaywallProducts() {
        this.isEditingProduct = false;
        try {
            this.paywallProducts = await getOrganizationProducts(PaymentTargetType.Paywall, topicData.topic?._id.$oid ?? '', topicData.topic?.organization_id?.$oid ?? '', topicData.topicKey);
        } catch (error) {
            console.error('Failed to load paywall products:', error);
            getMainPage().errorToastElement.showToast('Failed to load paywall products.');
            this.paywallProducts = [];
        }
    }

    handleChangeTab(e: CustomEvent) {
        this.tabIndex = e.detail.tabIndex;
        this.selected_tab = this.options[this.tabIndex];
    }

    openUpgradeDialog(e: CustomEvent) {
        this.dispatchEvent(
            new CustomEvent('open-upgrade-dialog', {
                detail: {
                    featureType: FeatureType.PaywallProtection,
                },
            })
        );
    }

    private async loadPaymentData() {
        if (!topicData.topic?.organization_id || !isDefined(organizationInfo.organization?._id)) {
            console.error('No organization ID found for topic.');
            return;
        }
        try {
            [this.stripeInfo, this.purchaseHistory] = await Promise.all([
                loadStripeStatus(organizationInfo.organization?._id.$oid ?? ''),
                getOrganizationPurchaseHistory(organizationInfo.organization?._id.$oid ?? '', topicData.topic?._id?.$oid ?? '', topicData.topicKey)
            ]);
        } catch (error) {
            getMainPage().errorToastElement.showToast('Failed to load purchases information.');
        }
    }

    async setEditedProduct(e: CustomEvent) {
        this.isEditingProduct = true;
        this.requestUpdate();
        await delay(100);
        const product = e.detail.product;
        if (this.createProductComponent) {
            await this.createProductComponent.setExistingProduct(product);
        }
    }

    renderProductCenter() {
        if (!(organizationInfo.organization?.is_administrator ?? false)) {
            return html`
                <settings-card header="Payment Settings">
                    <p class="info-paragraph">You do not have the required permissions to view or manage products and purchases. Please contact your organization administrator for access.</p>
                </settings-card>
            `;
        }

        return html`
            <settings-card header="Create Paywalls">
                <p class="info-paragraph">Get paid to do what you love. Set up a paywall with custom pricing options and track revenue generated through paywalls.</p>
                    <create-product-component
                    id="createProductComponent"
                        .payment_target_type=${PaymentTargetType.Paywall}
                        .stripeInfo=${this.stripeInfo}
                        .payment_target_entity_id=${topicData.topic?._id.$oid ?? ''}
                        @product-updated="${this.getPaywallProducts}"
                    >
                    </create-product-component>
            </settings-card>
            <settings-card header="Existing Paywalls and Prices">
                <p class="info-paragraph">View and manage existing paywalls and prices</p>
                ${this.paywallProducts.length > 0 ? html`
                    <existing-products-component
                        .data="${this.paywallProducts}"
                        .payment_target_type=${PaymentTargetType.Paywall}
                        .purchases=${this.purchaseHistory?.transactions ?? []}
                        @copy-payment-link="${async (e: CustomEvent) => await this.copyPaymentLink(e.detail.gate_id)}"
                        @set-edited-product="${this.setEditedProduct}"
                        @update-products="${this.getPaywallProducts}"
                    >
                    </existing-products-component>
                ` : html`
                    <p>No products found.</p>
                `}
            </settings-card>
            <settings-card header="Completed Purchases">
                <p class="info-paragraph">View all completed purchases made by users.</p>
                ${isDefined(this.purchaseHistory) && this.purchaseHistory.transactions.length > 0 ? html`
                    <completed-purchases-component
                        .data=${this.purchaseHistory}
                    ></completed-purchases-component>
                ` : html`
                    <p>No completed purchases found. If you believe this is an error, please contact support.</p>
                `}
            </settings-card>
        `;
    }

    render() {
        if (!topicData.isReady || !pointCache.isReady) {
            return html`<loading-element loadingString="Getting Paywall Data..."></loading-element>`;
        }
        return html`
            <div class="wrapper">
                <div class="header-title-and-buttons" style="background-color: ${topicData.backgroundColor}; color: ${topicData.textColor}">
                    <div class="left-header-content">
                    </div>
                    <div class="middle-header-content">
                        Paywalls for ${topicData.topic?.name}
                    </div>
                    <div class="right-header-content">
                    </div>
                </div>
                <div class="tabs">
                    <tabs-component
                        .options=${this.options}
                        .tabIndex=${this.selected_tab === 'Paywalls' ? 0 : this.selected_tab === 'Preview Points' ? 1 : 2}
                        @on-select-tab=${this.handleChangeTab}
                    ></tabs-component>
                </div>
                <div class="tab__content">
                    ${this.selected_tab === 'Paywalls' ? this.renderProductCenter() : ''}
                    ${this.selected_tab === 'Preview Points' ? this.renderPreviewPoints() : ''}
                    ${this.selected_tab === 'Webhooks (Coming Soon)' ? this.renderWebhooks() : ''}
                </div>
            </div>
        `;
    }

    private async copyPaymentLink(gate_id: string) {
        // create url with topic_id, topic key and gate_id

        let link = '';
        if (topicData.topic?.discovery_level === DiscoveryLevel.Private && topicData.topic?.cc_read_link !== undefined) {
            link = topicData.topic?.cc_read_link;
        } else if (topicData.topic?.cc_keyless_link !== undefined) {
            link = topicData.topic?.cc_keyless_link;
        }

        if (link === '' || link === undefined) {
            getMainPage().errorToastElement?.showToastAndThrow('Error getting payment link.');
            return;
        }

        // workaround to append gate_id to redirectURL clean up when aaron changes back end to accept whole parameter string
        const readurl = link;
        const gate_id_string = `${gate_id}`;
        const encoded_gate_id = encodeURIComponent(gate_id_string);
        const url = `${readurl}?gate_id=${encoded_gate_id}`;
        await navigator.clipboard.writeText(url);

        getMainPage().errorToastElement?.showToast('Copied Payment Link to clipboard.');
    }

    private async resetAfterSavePreviewPoints() {

        const newPointsSinceLastUpdate = await getAllPointsPaginated(
            this.amplitude_callsite,
            topicData.topicId,
            topicData.topicKey,
            undefined,
            false
        );
        this.previewedPoints = newPointsSinceLastUpdate?.filter((point) => !point.is_gate_hidden);
    }

    renderPreviewPoints() {
        return html`
            <settings-card header="Preview Points">
                <p class="info-paragraph">Manage which points are available for preview to users who have not gained access to your map.</p>
                <p class="info-paragraph">
                    If desired, you can select points that will be available for preview to users who have not gained access to your map. This helps them learn what the content on the map may look like.
                </p>
                <p class="info-paragraph">
                    Currently, <b>${pointCache.allPoints?.filter(
                        (point) => !point.is_gate_hidden
                    ).length}</b> of your <b>${pointCache.allPoints.length} </b> points are available for the public to view.
                </p>
                <gated-access-select-points-modal
                    .selectedPoints=${this.previewedPoints}
                    @close="${this.resetAfterSavePreviewPoints}">
                </gated-access-select-points-modal>
            </settings-card>
        `;
    };

    renderWebhooks() {
        return html`
            <settings-card header="Webhooks">
                <p class="info-paragraph">Webhooks functionality is coming soon. This feature will allow you to set up automated notifications for various events related to your paywalls and purchases.</p>
                <p class="info-paragraph">If you have specific requirements or use cases for webhooks, please contact our support team to share your feedback.</p>
                <form-button size="xs" variant="outline" @click="${() => openChatWindow('Paywall Webhooks Inquiry', 'User is interested in Paywall Webhooks feature.')}" .textColor=${topicData.textColor}>
                    Contact Support
                </form-button>
            </settings-card>
        `;
    }


    static get styles() {
        return css`
            .wrapper {
                display: flex;
                flex-direction: column;
                overflow: hidden auto;
                box-sizing: border-box;
                width: 100%;
                height: 100%
            }

            .header-title-and-buttons {
                display: flex;
                flex-direction: row;
                align-items: center;
                justify-content: space-between;
                padding: 10px;
                font-size: 18px;
                background-color: var(--theme-border-color);
                border-bottom: 1px solid var(--theme-border-color);
            }

            .left-header-content {
                display: flex;
                flex-direction: row;
                align-items:center;
                font-size: 0.8rem;
                gap: 5px;
                flex: 1;
                flex-wrap: wrap;
            }

            .checkboxHolder {
                display: flex;
                flex-direction: row;
                align-items: center;
            }

            .right-header-content {
                display: flex;
                flex-direction: row;
                align-items:center;
                font-size: 0.8rem;
                gap:5px;
                justify-content: flex-end;
                flex: 1;
                flex-wrap: wrap;
            }
            .modalContent {
                position: relative;
                width: 100%;
                height: auto;
                overflow-y: auto;
                box-sizing: border-box;
                height: 100%;
                display: flex;
                flex-direction: column;
            }

            .middle-header-content {
                flex: 1;
                font-size: 1rem;
                text-align:center;
            }

            .header-title-and-buttons h4 {
                margin: 5px 0;
            }

            .tabs {
                display: flex;
                align-items: center;
                justify-content: center;
                padding: 0 20px;
                background-color: var(--theme-white-color);
                border-bottom: 1px solid var(--theme-border-color);
                box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
            }

            .tab__content {
                padding: 20px;
                display: flex;
                gap: 20px;
                flex: 1;
                overflow: hidden auto;
                justify-content: flex-start;
                align-items: stretch;
                background-color: var(--theme-border-color);
                flex-direction: column;
            }

            /* width */
            .tab__content::-webkit-scrollbar {
                width: 5px;
            }

            /* Track */
            .tab__content::-webkit-scrollbar-track {
                background: var(--theme-light-scrollbar-color);
            }

            /* Handle */
            .tab__content::-webkit-scrollbar-thumb {
                background: var(--main-moon-color);
            }

            .manage-participants-and-results {
                display: flex;
                flex-direction: row;
                gap: 10px;
                height: 100%;
            }


            .category__wrapper {
                display: flex;
                flex-wrap: wrap;
                align-items: center;
                margin-top: 0.5rem;
            }

            .category__item {
                position: relative;
                user-select: none;
                cursor: pointer;
                display: flex;
                justify-content: center;
                align-items: center;
                padding: 3px 14px 3px 10px;
                border-radius: 1.25rem;
                margin-right: 6px;
                margin-bottom: 4px;
                font-size: 10px;
                border: 1px solid var(--main-primary-color);
            }

            .category__icon {
                border-radius: 999px;
                width: 1rem;
                height: 1rem;
                font-size: 16px;
                display: flex;
                align-items: center;
                justify-content: center;
                position: absolute;
                top: -5px;
                right: -5px;
                background: var(--theme-white-color);
                border: 1px solid var(--main-primary-color);
                color: var(--main-primary-color);
            }

            .disabled {
                background-color: var(--theme-border-color);
                color: var(--theme-input-color);
            }

            .menuPng {
                margin-right: 0.25rem;
                height: 15px;
                margin-top: auto;
            }

            .info-paragraph {
                color: var(--theme-input-color);
                margin-top: 0;
                margin-bottom: 0;
            }

            .sub-title {
                margin-bottom: 0px;
                color: var(--theme-input-color);
                display: inline-flex;
                --mdc-icon-size: 16px;
            }

            h4.sub-title {
                font-weight: 600;
                font-size: 12px;
                line-height: 100%;
                color: #000000;
                text-align: left;
                margin: 15px 0 5px;
                display: flex;
                align-items: center;
                flex-direction: row;
            }

            .election-details__tabs {
                width: 100%;
                display: flex;
                align-items: center;
                justify-content: center;
                border-radius: 20px;
                border: 1px solid var(--main-primary-color);
            }

            .election-details__tab {
                flex: 1;
                display: flex;
                align-items: center;
                justify-content: center;
                line-height: 150%;
                color: var(--main-primary-color);
                background: transparent;
                padding: 5px 10px;
                cursor: pointer;
            }

            .election-details__tab.right {
                border-radius: 0px 20px 20px 0px;
            }

            .election-details__tab.left {
                border-radius: 20px 0px 0px 20px;
            }

            .election-details__tab.active {
                background: var(--main-primary-color);
                color: var(--theme-white-color);
            }

            .tab-icon {
                width: 12px;
                height: 12px;
                margin-right: 5px;
            }

            .tab-icon.small {
                width: 12px;
                height: 12px;
            }

            .input__election-title {
                width: 100%;
                margin-top: 15px;
            }

            p {
                margin-top: 5px;
                margin-bottom: 5px;
            }

            .addon__footer {
                display: flex;
                flex-direction: row;
                justify-content: flex-end;
                flex-wrap: wrap;
                align-items: center;
                padding: 0 20px;
                gap: 10px;
            }

            .challeng-save-button {
                --mdc-theme-primary: var(--main-success-color);
                --mdc-typography-button-text-transform: case;
                margin: 10px 0;
            }

            .date-container {
                width: 100%;
                display: flex;
                align-items: flex-start;
                justify-content: space-between;
            }

            .input__date {
                width: 48%;
            }

            .checkbox-wrapper {
                width: 100%;
                display: flex;
                align-items: center;
                justify-content: flex-start;
                gap: 2px;
                line-height: 120%;
                color: rgb(16, 44, 61);
                margin-top: 5px;
            }

            .checkbox-wrapper span {
                margin-bottom: 4px;
            }

            .edit__textfield {
                width: 100%;
                margin-top: 15px;
            }

            h4 {
                margin-bottom: 0px;
                color: var(--theme-input-color);
            }

            .awards-wrapper {
                margin: 20px 0;
            }

            .button__wrapper {
                width: 100%;
                display: flex;
                align-items: flex-start;
                justify-content: flex-end;
                margin: 15px 0;
            }

            .hidden {
                display: none;
            }

            .view_participants_progress {
                border: 1px solid var(--main-primary-color);
                --mdc-theme-on-primary: var(--main-primary-color);
                --mdc-theme-primary: transparent;
                --mdc-button-height: 50px;
                --mdc-typography-button-font-size: 12px;
                --mdc-typography-button-text-transform: capitalize;
            }

            .not-full-width {
                width: unset;
            }

            .redButton {
                --mdc-theme-primary: var(--main-invalid-color);
            }


            .redText {
                color: var(--main-invalid-color);
            }

            .election-save-button {
                font-size: 16px;
                border-radius: 8px;
                --mdc-theme-on-primary: var(--theme-white-color);
                --mdc-typography-button-text-transform: capitalize;
                --mdc-theme-primary: #5EA667;
            }

            .greyed-out {
                --mdc-theme-primary: grey;
            }

            .setting-switch {
                align-items: center;
                display: flex;
                gap: 8px;
                margin: 0.5rem auto;
            }

            .switch-label {
                text-align: left;
            }

            .switch-label.left {
                text-align: right
            }

            .stats-card-container {
                width: 100%;
                display: flex;
                align-items: center;
                justify-content: flex-start;
                box-sizing: border-box;
                flex-wrap: wrap;
            }

            .standings-table-container {
                width: 100%
            }

            .standings-row__votes {
                flex: 1
            }

            .standings-row__name {
                flex: 2
            }

            .download__options {
                width: 100%;
                display: flex;
                flex-direction: row;
                align-items: center;
                justify-content: flex-start;
                gap: 10px;
                flex-wrap: wrap;
            }

            .standings-row {
                display: flex;
                gap: 10px;
                justify-content: flex-start;
                border: 1px black solid;
                width: 100%;
                padding: 5px 2px;
            }

            .stats-card {
                width: 100%;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                border: 1px solid #cac4d0;
                border-radius: 12px;
                box-sizing: border-box;
                height: 50px;
            }

            .stats-card.third {
                width: calc(33.33% - 12px);
                margin: 15px 5px;
                justify-content: space-between;
            }

            .stats-card__value {
                font-size: 24px;
                line-height: 130%;
                color: #000000;
                font-weight: 700;
                maring-right: 4px;
            }

            .stats-card__label {
                line-height: 125%;
                color: #1d1b20;
            }

            .awards-earned {
                width: 100%;
                text-align: center;
                font-weight: 700;
                font-size: 16px;
                line-height: 150%;
                color: #000000;
                margin: 15px 0;
            }

            .awards__wrapper {
                max-width: 100%;
                display: flex;
                align-items: flex-start;
                justify-content: flex-start;
                overflow-x: auto;
            }

            .awards__card {
                display: flex;
                flex-direction: column;
                align-items: center;
                padding: 0px 5px;
                gap: 5px;
                flex: 1;
            }

            .award-icon {
                width: 37px;
                height: 37px;
            }

            .award-count {
                font-weight: 700;
                font-size: 18px;
                line-height: 130%;
                color: #000000;
                text-align: center;
            }

            .awards__label {
                text-align: center;
            }

            #assignElectionCheckinsDrawer,
            #electionParticipantsProgressDrawer,
            #electionParticipantDetailsDrawer {
                --bottom-height: 0px;
                --bottom-height-half: 0px;
            }

            .challenges-wrapper {
                width: 100%;
                overflow-y: auto;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: flex-start;
                box-sizing: border-box;
            }

            .challenge-row {
                width: 100%;
                display: flex;
                align-items: center;
                justify-content: space-between;
                padding: 8px 5px;
                box-sizing: border-box;
                border-bottom: 1px solid #DADADA;
                gap: 10px;
            }

            .challenge-row:hover {
                background-color: var(--theme-border-color);
            }

            .header-row {
                cursor: pointer;
                width: 100%;
                align-items: center;
                font-weight: 700;
                display: flex;
                height: 40px;
                background-color: var(--theme-border-color);
                gap: 10px;
            }

            .header-row mwc-icon {
                font-size: 20px;
            }

            .header-cell,
            .data-cell {
                align-items: center;
            }

            .header-cell {
                display: flex;
                align-items: center;
                padding: 0 10px;
            }

            .header-cell.text {
                text-align: left;
                width: 15%;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .header-cell.number {
                text-align: center;
                width: 12%;
                justify-content: center;
            }

            .header-cell.primary {
                flex: 1;
            }

            .data-cell {
                display: flex;
                align-items: center;
                padding: 0 10px;
            }

            .data-cell.text {
                text-align: left;
                width: 15%;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .data-cell.number {
                text-align: center;
                width: 12%;
                justify-content: center;
            }

            .data-cell.primary {
                flex: 1;
            }

            .action-button {
                font-size: 16px;
                border-radius: 8px;
                margin: 10px 0;
                --mdc-typography-button-text-transform: capitalize;
            }

            .aqua-blue {
                --mdc-theme-primary: var(--main-secondary-color);
            }

        `;
    }
}
